import React, { useState, useEffect, useRef } from "react";
import { Drawer, Button, Collapse } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import logo from "../utils/Logo/Teople_Technologies-final.webp";
import "../Style/Navbar.css";
import { Layout } from "antd";

const { Header, Content } = Layout;
const { Panel } = Collapse;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [expertiseDropdownVisible, setExpertiseDropdownVisible] = useState(false);
  const [activeCollapseKey, setActiveCollapseKey] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const lastScrollY = useRef(window.scrollY);
  const [navTheme, setNavTheme] = useState("dark");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setDropdownVisible(false);
        setExpertiseDropdownVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (location.pathname === "/about") {
      setNavbarVisible(true);
      return;
    }
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
        setNavbarVisible(false);
      } else {
        setNavbarVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1000);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname === "/about") {
      setNavTheme("about-white");
      const handleScroll = () => {
        const triggerHeight = window.innerHeight * 0.7;
        setNavTheme(window.scrollY > triggerHeight ? "about-transparent" : "about-white");
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    } else if (["/services", "/staffing-solution", "/web-development", "/mobile-development", "/cloud-services", "/product-development", "/hw-sw-integration", "/ui-design", "/iot-development", "/ai-ml-development", "/data-science"].includes(location.pathname)) {
      setNavTheme("services-blue");
      const handleScroll = () => {
        const triggerHeight = window.innerHeight * 0.7;
        setNavTheme(window.scrollY > triggerHeight ? "about-transparent" : "services-blue");
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    } else if (location.pathname === "/") {
      setNavTheme("home-light");
      const handleScroll = () => {
        const triggerHeight = window.innerHeight * 0.9;
        setNavTheme(window.scrollY > triggerHeight ? "light" : "home-light");
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    } else {
      setNavTheme("light");
    }
  }, [location.pathname]);

  const expertiseList = [
    { name: "Java", path: "/expertise/java", icon: "logos:java" },
    { name: "Spring Boot", path: "/expertise/spring-boot", icon: "logos:spring-icon" },
    { name: "NodeJs", path: "/expertise/node-js", icon: "logos:nodejs-icon" },
    { name: "Python", path: "/expertise/python", icon: "logos:python" },
    { name: "JavaScript", path: "/expertise/java-script", icon: "logos:javascript" },
    { name: "ReactJs", path: "/expertise/react-js", icon: "logos:react" },
    { name: "Angular", path: "/expertise/angular", icon: "logos:angular-icon" },
    { name: "PWA", path: "/expertise/pwa", icon: "carbon:progressive-web-app" },
    { name: "AWS", path: "/expertise/aws", icon: "logos:aws" },
    { name: "MySQL", path: "/expertise/my-sql", icon: "logos:mysql" },
    { name: "GitLab", path: "/expertise/git-lab", icon: "logos:gitlab" },
    { name: "Tailwind", path: "/expertise/tailwind", icon: "logos:tailwindcss-icon" },
    { name: "Docker", path: "/expertise/docker", icon: "logos:docker-icon" },
    { name: "Jenkins", path: "/expertise/jenkins", icon: "logos:jenkins" },
    { name: "Vue.js", path: "/expertise/vuejs", icon: "logos:vue" },
    { name: "Jira", path: "/expertise/jira", icon: "logos:jira" },
    { name: "AI & ML", path: "/expertise/ai-ml", icon: "carbon:ai-results" },
    { name: "Data Science", path: "/expertise/data-science", icon: "carbon:data-analytics" }
  ];

  const servicesList = [
    { name: "Staffing Solution", path: "/staffing-solution", icon: "carbon:user-certification" },
    { name: "Web Development", path: "/web-development", icon: "carbon:application-web" },
    { name: "Mobile Development", path: "/mobile-development", icon: "carbon:mobile" },
    { name: "Cloud Services", path: "/cloud-services", icon: "carbon:cloud-services" },
    { name: "Product Development", path: "/product-development", icon: "carbon:product" },
    { name: "Hw-sw integration", path: "/hw-sw-integration", icon: "carbon:chip" },
    { name: "UI Design", path: "/ui-design", icon: "carbon:paint-brush" },
    { name: "Iot Development", path: "/iot-development", icon: "carbon:iot-platform" },
    { name: "AI & ML Development", path: "/ai-ml-development", icon: "carbon:machine-learning" },
    { name: "Data Science", path: "/data-science", icon: "carbon:data-analytics" }
  ];

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setDropdownVisible(false);
    setDrawerVisible(false);
    setActiveCollapseKey([]);
    if (location.pathname === service.path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(service.path, { state: { service } });
    }
  };

  const handleLinkClick = (expertise) => {
    setExpertiseDropdownVisible(false);
    setDrawerVisible(false);
    setActiveCollapseKey([]);
    navigate(expertise.path);
  };

  const showDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => {
    setDrawerVisible(false);
    setActiveCollapseKey([]);
  };

  return (
    <>
      <Header className={`navbarbg ${navTheme} ${navbarVisible ? "visible" : "hidden"}`}>
        <Link to="/" style={{ display: "flex", alignItems: "center" }}>
          <img className="teople-logo" src={logo} alt="Logo" />
        </Link>

        {!isMobile && (
          <div className="desktop-navbar" style={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: "2em", marginRight: "2em" }}>
            <Link className={location.pathname === "/" ? "nav-link active" : "nav-link"} to="/">Home</Link>
            <Link className={location.pathname === "/about" ? "nav-link active" : "nav-link"} to="/about">About Us</Link>
            <Link className={location.pathname === "/our-work" ? "nav-link active" : "nav-link"} to="/our-work">Our Work</Link>
            
            <div className="nav-dropdown">
              <Link className={servicesList.some(s => location.pathname === s.path) ? "nav-link active" : "nav-link"} to="/service-section" onClick={(e) => { e.preventDefault(); setExpertiseDropdownVisible(false); setDropdownVisible(!dropdownVisible); }}>
                Our Services <span className={`dropdown-arrow ${dropdownVisible ? 'rotated' : ''}`}>▼</span>
              </Link>
              <div className={`dropdown-menu ${dropdownVisible ? 'show' : ''}`}>
                <div className="dropdown-header">
                  <h2>Services</h2>
                  <p>Crafting Success Stories, One Project at a Time.</p>
                </div>
                <div className="dropdown-content">
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:application-web" />Development</div>
                    <Link className="dropdown-item" to="/web-development" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[1]); }}>Web Development</Link>
                    <Link className="dropdown-item" to="/mobile-development" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[2]); }}>Mobile Development</Link>
                    <Link className="dropdown-item" to="/ui-design" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[6]); }}>UI/UX Design</Link>
                    <Link className="dropdown-item" to="/product-development" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[4]); }}>Product Development</Link>
                  </div>
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:cloud" />Cloud & IoT</div>
                    <Link className="dropdown-item" to="/cloud-services" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[3]); }}>Cloud Services</Link>
                    <Link className="dropdown-item" to="/iot-development" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[7]); }}>IoT Development</Link>
                    <Link className="dropdown-item" to="/hw-sw-integration" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[5]); }}>HW-SW Integration</Link>
                  </div>
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:ai-results" />AI & Data</div>
                    <Link className="dropdown-item" to="/ai-ml-development" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[8]); }}>AI & ML Development</Link>
                    <Link className="dropdown-item" to="/data-science" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[9]); }}>Data Science</Link>
                    <Link className="dropdown-item" to="/staffing-solution" onClick={() => { setDropdownVisible(false); handleServiceClick(servicesList[0]); }}>Staffing Solution</Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="nav-dropdown">
              <Link className={expertiseList.some(e => location.pathname === e.path) ? "nav-link active" : "nav-link"} onClick={(e) => { e.preventDefault(); setDropdownVisible(false); setExpertiseDropdownVisible(!expertiseDropdownVisible); }}>
                Our Expertise <span className={`dropdown-arrow ${expertiseDropdownVisible ? 'rotated' : ''}`}>▼</span>
              </Link>
              <div className={`dropdown-menu ${expertiseDropdownVisible ? 'show' : ''}`}>
                <div className="dropdown-header">
                  <h2>Expertise</h2>
                  <p>Mastering Technologies to Build Your Vision.</p>
                </div>
                <div className="dropdown-content">
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:code" />Backend</div>
                    <Link className="dropdown-item" to="/expertise/java" onClick={() => { handleLinkClick(expertiseList[0]); setExpertiseDropdownVisible(false); }}>Java</Link>
                    <Link className="dropdown-item" to="/expertise/spring-boot" onClick={() => { handleLinkClick(expertiseList[1]); setExpertiseDropdownVisible(false); }}>Spring Boot</Link>
                    <Link className="dropdown-item" to="/expertise/node-js" onClick={() => { handleLinkClick(expertiseList[2]); setExpertiseDropdownVisible(false); }}>NodeJs</Link>
                    <Link className="dropdown-item" to="/expertise/python" onClick={() => { handleLinkClick(expertiseList[3]); setExpertiseDropdownVisible(false); }}>Python</Link>
                  </div>
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:application" />Frontend</div>
                    <Link className="dropdown-item" to="/expertise/java-script" onClick={() => handleLinkClick(expertiseList[4])}>JavaScript</Link>
                    <Link className="dropdown-item" to="/expertise/react-js" onClick={() => handleLinkClick(expertiseList[5])}>ReactJs</Link>
                    <Link className="dropdown-item" to="/expertise/angular" onClick={() => handleLinkClick(expertiseList[6])}>Angular</Link>
                    <Link className="dropdown-item" to="/expertise/vuejs" onClick={() => handleLinkClick(expertiseList[14])}>Vue.js</Link>
                    <Link className="dropdown-item" to="/expertise/tailwind" onClick={() => handleLinkClick(expertiseList[11])}>Tailwind CSS</Link>
                  </div>
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:cloud-services" />Cloud & DB</div>
                    <Link className="dropdown-item" to="/expertise/aws" onClick={() => handleLinkClick(expertiseList[8])}>AWS</Link>
                    <Link className="dropdown-item" to="/expertise/my-sql" onClick={() => handleLinkClick(expertiseList[9])}>MySQL</Link>
                    <Link className="dropdown-item" to="/expertise/pwa" onClick={() => handleLinkClick(expertiseList[7])}>PWA</Link>
                  </div>
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:container-software" />DevOps</div>
                    <Link className="dropdown-item" to="/expertise/docker" onClick={() => handleLinkClick(expertiseList[12])}>Docker</Link>
                    <Link className="dropdown-item" to="/expertise/jenkins" onClick={() => handleLinkClick(expertiseList[13])}>Jenkins</Link>
                    <Link className="dropdown-item" to="/expertise/git-lab" onClick={() => handleLinkClick(expertiseList[10])}>GitLab</Link>
                    <Link className="dropdown-item" to="/expertise/jira" onClick={() => handleLinkClick(expertiseList[15])}>Jira</Link>
                  </div>
                  <div className="dropdown-column">
                    <div className="dropdown-column-title"><Icon icon="carbon:machine-learning" />AI & Data</div>
                    <Link className="dropdown-item" to="/expertise/ai-ml" onClick={() => handleLinkClick(expertiseList[16])}>AI & ML</Link>
                    <Link className="dropdown-item" to="/expertise/data-science" onClick={() => handleLinkClick(expertiseList[17])}>Data Science</Link>
                  </div>
                </div>
              </div>
            </div>

            <Link className={location.pathname === "/blog" ? "nav-link active" : "nav-link"} to="/blog">Blog</Link>
            <Link className={location.pathname === "/career" ? "nav-link active" : "nav-link"} to="/career">Career</Link>
            <Link className={location.pathname === "/contact-us" ? "nav-contact-btn active" : "nav-contact-btn"} to="/contact-us">Contact us</Link>
          </div>
        )}

        {isMobile && <Button className="mobile-menu-button" type="primary" icon={<MenuOutlined />} onClick={showDrawer} />}
      </Header>

      <Drawer placement="left" onClose={closeDrawer} open={drawerVisible} width={280} bodyStyle={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', height: '100%' }}>
        <Link className={`drawer-link ${location.pathname === "/" ? "active" : ""}`} to="/" onClick={closeDrawer}>Home</Link>
        <Link className={`drawer-link ${location.pathname === "/about" ? "active" : ""}`} to="/about" onClick={closeDrawer}>About Us</Link>
        <Collapse ghost size="small" accordion activeKey={activeCollapseKey} onChange={setActiveCollapseKey} expandIcon={({ isActive }) => <Icon icon={isActive ? "mdi:chevron-up" : "mdi:chevron-down"} />} className="drawer-collapse">
          <Panel header={<div className="collapse-header">Our Services</div>} key="services">
            {servicesList.map((service, index) => (
              <Link key={index} className="collapse-item" to={service.path} onClick={() => handleServiceClick(service)}>
                <Icon icon={service.icon} />
                <span>{service.name}</span>
              </Link>
            ))}
          </Panel>
          <Panel header="Our Expertise" key="expertise">
            {expertiseList.map((expertise, index) => (
              <Link key={index} className="collapse-item" to={expertise.path} onClick={() => handleLinkClick(expertise)}>
                <Icon icon={expertise.icon} />
                <span>{expertise.name}</span>
              </Link>
            ))}
          </Panel>
        </Collapse>
        <Link className={`drawer-link ${location.pathname === "/our-work" ? "active" : ""}`} to="/our-work" onClick={closeDrawer}>Our Work</Link>
        <Link className={`drawer-link ${location.pathname === "/blog" ? "active" : ""}`} to="/blog" onClick={closeDrawer}>Blog</Link>
        <Link className={`drawer-link ${location.pathname === "/career" ? "active" : ""}`} to="/career" onClick={closeDrawer}>Career</Link>
        <Link className={`drawer-link ${location.pathname === "/contact-us" ? "active" : ""}`} to="/contact-us" onClick={closeDrawer}>Contact Us</Link>
      </Drawer>

      <Content style={{ paddingTop: "0px" }}>
        {selectedService && <div className="service-details"></div>}
      </Content>
    </>
  );
};

export default Navbar;
